import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * AdminAdvisoryBlast — send a branded regulatory / client advisory to
 * every confirmed subscriber (optionally filtered by source).
 *
 * Backend endpoints:
 *   POST /api/admin/advisory-blast/preview  → { recipient_count, subject, html, dispatch_enabled }
 *   POST /api/admin/advisory-blast/send     → { sent, failed_count, ... }
 *   GET  /api/admin/advisory-blast/runs     → previous 50 dispatches
 *
 * Both preview and send are behind require_admin. Sends are gated on
 * EMAIL_DISPATCH_ENABLED — preview is always available.
 */
const DEFAULT_FORM = {
  kicker: "Regulatory Advisory · August 2026",
  subject: "FinCEN Ends the CTA — a note from the firm",
  headline: "FinCEN permanently ends CTA beneficial ownership reporting.",
  lede:
    "On August 11, 2026, Treasury issued a final rule that removes the beneficial ownership reporting obligation for U.S. companies and U.S. persons — and directed FinCEN to delete previously reported information. A short advisory follows for your file.",
  body_html:
    "<p style='font-size:15px;line-height:1.6;color:#333;'>The final rule takes effect on publication in the Federal Register. If your entity was formed in the U.S. and has only U.S. owners, no further filings are required. Foreign entities that are reporting companies must still report beneficial ownership information for their foreign individual owners.</p><p style='font-size:15px;line-height:1.6;color:#333;'>We are reaching out to each of our business-formation clients individually. Please read the full advisory below for the practical checklist we are running with each client this week.</p>",
  cta_label: "Read the Full Advisory",
  cta_url: "https://lonestarcounsel.com/journal/fincen-ends-cta-boi-reporting-2026",
  target_source: "",
};

const SOURCE_OPTIONS = [
  { value: "", label: "All confirmed subscribers" },
  { value: "regulatory_advisories", label: "Regulatory Advisories only" },
  { value: "state_of_the_firm", label: "State of the Firm only" },
  { value: "journal", label: "Journal essays only" },
];

export default function AdminAdvisoryBlast() {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmingSend, setConfirmingSend] = useState(false);
  const [runs, setRuns] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/api/admin/advisory-blast/runs");
        setRuns(r.data || []);
      } catch (_) { /* first run — collection may be empty */ }
    })();
  }, []);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const doPreview = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const r = await api.post("/api/admin/advisory-blast/preview", form);
      setPreview(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPreviewing(false);
    }
  };

  const doSend = async () => {
    setSending(true);
    try {
      const r = await api.post("/api/admin/advisory-blast/send", form);
      toast.success(`Advisory sent — ${r.data.sent} of ${r.data.recipient_count} succeeded.`);
      setConfirmingSend(false);
      setPreview(null);
      // Refresh runs
      const runsResp = await api.get("/api/admin/advisory-blast/runs");
      setRuns(runsResp.data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="admin-advisory-blast">
      {/* --- Form --- */}
      <div className="lg:col-span-6 space-y-6">
        <div>
          <div className="kicker">Client Advisory Blast</div>
          <h2 className="mt-3 font-display text-3xl leading-[1.05]">
            Send a branded advisory to your confirmed subscribers.
          </h2>
          <p className="mt-3 text-slate2 font-cormorant italic">
            Preview renders instantly. Sending is gated on <code>EMAIL_DISPATCH_ENABLED</code>
            and the sender domain being verified in Resend.
          </p>
        </div>

        <label className="block">
          <div className="field-label mb-2">Subject line (inbox preview)</div>
          <input className="field" value={form.subject} onChange={update("subject")} data-testid="advisory-subject" />
        </label>

        <label className="block">
          <div className="field-label mb-2">Kicker (small-caps line above the headline)</div>
          <input className="field" value={form.kicker} onChange={update("kicker")} data-testid="advisory-kicker" />
        </label>

        <label className="block">
          <div className="field-label mb-2">Headline</div>
          <input className="field" value={form.headline} onChange={update("headline")} data-testid="advisory-headline" />
        </label>

        <label className="block">
          <div className="field-label mb-2">Lede paragraph (italic, sits under the headline)</div>
          <textarea rows={4} className="field" value={form.lede} onChange={update("lede")} data-testid="advisory-lede" />
        </label>

        <label className="block">
          <div className="field-label mb-2">Body HTML (optional — full paragraphs)</div>
          <textarea rows={6} className="field font-mono text-xs" value={form.body_html} onChange={update("body_html")} data-testid="advisory-body-html" />
        </label>

        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <div className="field-label mb-2">Call-to-action label</div>
            <input className="field" value={form.cta_label} onChange={update("cta_label")} data-testid="advisory-cta-label" />
          </label>
          <label className="block">
            <div className="field-label mb-2">Call-to-action URL</div>
            <input className="field" value={form.cta_url} onChange={update("cta_url")} data-testid="advisory-cta-url" />
          </label>
        </div>

        <label className="block">
          <div className="field-label mb-2">Target audience</div>
          <select className="field" value={form.target_source} onChange={update("target_source")} data-testid="advisory-target-source">
            {SOURCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>

        <div className="flex flex-wrap gap-3 pt-2">
          <button className="btn-primary" onClick={doPreview} disabled={previewing} data-testid="advisory-preview-btn">
            {previewing ? "Rendering…" : "Preview + Count Recipients"}
          </button>
          {preview && (
            <button
              className="btn-brass"
              onClick={() => setConfirmingSend(true)}
              disabled={sending || !preview.dispatch_enabled}
              data-testid="advisory-send-btn"
              title={preview.dispatch_enabled ? "" : "EMAIL_DISPATCH_ENABLED is false — sending is off."}
            >
              Send to {preview.recipient_count} subscribers &rarr;
            </button>
          )}
        </div>
      </div>

      {/* --- Preview + Runs --- */}
      <div className="lg:col-span-6 space-y-8">
        {preview && (
          <div data-testid="advisory-preview">
            <div className="kicker">Rendered Preview</div>
            <div className="mt-3 text-xs text-slate2 font-cormorant italic">
              {preview.recipient_count} matching subscribers · dispatch{" "}
              {preview.dispatch_enabled ? "enabled" : "DISABLED (env)"}
            </div>
            <iframe
              srcDoc={preview.html}
              title="Advisory Preview"
              className="mt-4 w-full h-[560px] border border-brass/30 bg-white"
              data-testid="advisory-preview-iframe"
            />
          </div>
        )}

        <div>
          <div className="kicker">Recent Dispatches</div>
          <ul className="mt-4 divide-y divide-brass/20" data-testid="advisory-runs-list">
            {runs.length === 0 && (
              <li className="py-4 text-slate2 font-cormorant italic">No advisories sent yet.</li>
            )}
            {runs.map((r) => (
              <li key={r.id} className="py-4">
                <div className="text-sm font-serif">{r.headline}</div>
                <div className="text-xs text-slate2 mt-1">
                  {new Date(r.started_at).toLocaleString()} · sent {r.sent}/{r.recipient_count}
                  {r.failed_count ? ` · ${r.failed_count} failed` : ""}
                  {r.target_source ? ` · ${r.target_source}` : " · all"}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Confirm modal */}
      {confirmingSend && (
        <div
          className="fixed inset-0 z-[200] bg-navy/70 backdrop-blur-sm flex items-center justify-center px-4"
          data-testid="advisory-confirm-modal"
        >
          <div className="bg-ivory max-w-lg w-full p-8 md:p-12">
            <div className="kicker">Confirm Send</div>
            <h3 className="mt-4 font-display text-3xl leading-[1.05]">
              Send this advisory to {preview?.recipient_count} subscribers?
            </h3>
            <p className="mt-4 text-slate2 font-cormorant italic">
              This is final. The advisory will be delivered by Resend to every confirmed subscriber
              matching the selected audience filter.
            </p>
            <div className="mt-8 flex gap-3">
              <button className="btn-ghost" onClick={() => setConfirmingSend(false)} data-testid="advisory-cancel-send">
                Cancel
              </button>
              <button className="btn-brass" onClick={doSend} disabled={sending} data-testid="advisory-confirm-send">
                {sending ? "Sending…" : "Yes — send now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
